import { useCallback } from 'react';

import {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@atlassian/conversation-assistant/utils';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { useCreateRovoExperienceSidebarController } from './useCreateRovoExperienceSidebarController';

/**
 * Note: this only works if Rovo is enabled, look up RovoButton as an example
 * @returns toggleConversationAssistant which will open the given mode of the Conversation Assistant
 */
export const useToggleConversationAssistant = () => {
	const rightSidebarContext = useRightSidebarContext();
	const { panelId } = rightSidebarContext.getCurrent() || {};
	const { isMiniModalAvailable, toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
	const isConversationAssistantSidebarActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID;
	const sidebarApi = useCreateRovoExperienceSidebarController();

	const toggleConversationAssistant = useCallback(
		(openChatMode: 'sidebar' | 'mini-modal' = 'sidebar') => {
			if (
				fg('aifc_create_enabled') ||
				expValEquals('rovo_chat_refresh_v1_experiment', 'isRenderRefreshEnabled', true)
			) {
				toggleConversationAssistantUIMode({
					targetMode:
						openChatMode === 'mini-modal' && isMiniModalAvailable
							? ConversationAssistantUIModes.MINI_MODAL
							: ConversationAssistantUIModes.SIDEBAR,
					sidebarApi,
				});
			} else {
				if (isConversationAssistantSidebarActive) {
					rightSidebarContext.close(CONVERSATION_ASSISTANT_CONTAINER_ID);
				} else {
					rightSidebarContext.open(
						CONVERSATION_ASSISTANT_CONTAINER_ID,
						CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
					);
				}
			}
		},
		[
			toggleConversationAssistantUIMode,
			isConversationAssistantSidebarActive,
			rightSidebarContext,
			sidebarApi,
			isMiniModalAvailable,
		],
	);

	return {
		toggleConversationAssistant,
		isConversationAssistantSidebarActive,
	};
};
